import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Box, Typography } from '@mui/material'
import { LibraryBooks } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { checkFile } from '../Utilities'
import { setFeatureFlag } from '../../api/dataSlice'
import { useGetAiParameterEnabledQuery, getAiParameterId } from '../../api/graphqlApi'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setReportActions } from '../chatbot/ChatManager'
import dayjs from 'dayjs'
import CameraSelection from '../controls/CameraSelection'
import PdfDialog from '../controls/PdfDialog'

// gated by MYSITE route access (role check skipped on `protected: false`) and AI parameter `PROJECT_REPORT_ENABLED` (default 27); the per-camera `project_report.pdf` HEAD check drives the disabled state and the inline "no file" alert above the button (and the `hasReport` Redux flag — auto-expand and chatbot capability stay gated on actual content)
export default function ReportAccordion({ expanded, onChangeExpanded, isPortrait, isMySite, initialCameraId, onCameraIdChange }) {

    const { t } = useTranslation()
    const { CAMERAS, LAYOUT: { TITLE }, BASEURL, ROUTES: { MYSITE } } = window.conf
    const { hasRouteAccess, isLoggedIn } = useKeycloak()

    const dispatch = useDispatch()
    const hasMysite = hasRouteAccess(MYSITE)

    const { data: reportAiParameter, isSuccess: reportAiParameterSuccess, isError: reportAiParameterError } = useGetAiParameterEnabledQuery(
        { aiParameterId: getAiParameterId('PROJECT_REPORT_ENABLED') },
        { skip: !hasMysite }
    )

    const [report, setReport] = useState({
        selectedCameraId: initialCameraId ?? CAMERAS?.[0]?.id ?? null, // initialCameraId (ArchiveControls) first -> survives mobile-portrait footer unmount
        checkedCameras: [],
        loading: false
    })
    const [openDialog, setOpenDialog] = useState(false)

    const setReportCameraId = (id) => setReport(prev => ({ ...prev, selectedCameraId: id }))
    const reportRef = useRef(report)
    reportRef.current = report

    // chatbot registry — refs feed live state without stale closures
    useEffect(() => {
        setReportActions({
            getSelectedCameraId: () => reportRef.current.selectedCameraId,
            getAvailableCameraIds: () => Object.keys(reportRef.current.checkedCameras)
                .filter(id => reportRef.current.checkedCameras[id].exists)
                .map(Number),
            getFileExists: () => !!(reportRef.current.checkedCameras[reportRef.current.selectedCameraId]?.exists),
            setSelectedCameraId: (id) => setReportCameraId(id),
            openDialog: () => setOpenDialog(true),
        })
        return () => setReportActions(null)
    }, [])

    // push local selection up so it persists across footer close/reopen
    useEffect(() => {
        onCameraIdChange?.(report.selectedCameraId)
    // onCameraIdChange is a stable setState from the parent — not in deps on purpose
    }, [report.selectedCameraId]) // eslint-disable-line react-hooks/exhaustive-deps
    
    const moreThanOneCam = CAMERAS.length > 1
    const selectedReport = report.checkedCameras[report.selectedCameraId] || {}
    const availableCameras = Object.keys(report.checkedCameras).filter(id => report.checkedCameras[id].exists).map(Number)
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedReport?.name
        ? `${selectedReport.name}_`
        : moreThanOneCam
        ? `${t("events.camera")} ${selectedReport.id + 1}_`
            : ""
    const targetFilename = `${title}${camera}${t("events.report")}_${dayjs(selectedReport.lastModified).format("YY-MM-DD")}`
    const filePath = selectedReport?.filePath
    const fileExists = selectedReport.exists || false
    const hasReport = hasMysite && reportAiParameter?.enabled
    const reportCameraIds = reportAiParameter?.cameraIds ?? []
    const canShowReport = hasReport && !report.loading && availableCameras.length > 0
    const reportAiParameterSettled = reportAiParameterSuccess || reportAiParameterError
    const reportReady = !hasMysite || (reportAiParameterSettled && (!reportAiParameter?.enabled || report.checkedCameras.length > 0))

    // publish feature flags so `useAccordionAutoExpand` can count Report as a candidate.
    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasReport', value: !!canShowReport }))
    }, [dispatch, canShowReport])

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'reportReady', value: !!reportReady }))
    }, [dispatch, reportReady])

    useEffect(() => {
        if (!hasReport) return

        let cancelled = false
        const checkAllReports = async () => {
            if (isMySite) {
                setReport(prev => ({ ...prev, loading: true }))

                const results = await Promise.all(
                    CAMERAS.map(async (elem) => {
                        const path = `${BASEURL}cam${elem.id + 1}/reports/project_report.pdf`
                        // first check AI parameter allowlist
                        if (!reportCameraIds.includes(elem.id)) {
                            return { id: elem.id, name: elem.name, exists: false, lastModified: null, filePath: path }
                        }
                        const { exists, contentType, lastModified } = await checkFile(path)
                        return {
                            id: elem.id,
                            name: elem.name,
                            exists: exists && contentType?.includes('pdf'),
                            lastModified,
                            filePath: path
                        }
                    })
                )

                if (cancelled) return
                const available = results.filter(elem => elem.exists)
                const firstAvailable = available[0]?.id ?? null

                setReport(prev => {
                    const stillValid = prev.selectedCameraId != null && available.some(elem => elem.id === prev.selectedCameraId) // preserve users previous choice if in available set
                    return {
                        selectedCameraId: stillValid ? prev.selectedCameraId : firstAvailable,
                        checkedCameras: results,
                        loading: false
                    }
                })
            }
        }

        checkAllReports()
        return () => { cancelled = true }
    // CAMERAS/BASEURL are stable window.conf values; checkFile is a stable import — not in deps on purpose.
    // reportCameraIds joined as a string dep so a change in the AI-enabled set re-triggers the check.
    }, [isMySite, hasReport, reportCameraIds.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <Accordion
                expanded={expanded === 'report'}
                onChange={onChangeExpanded('report')}
                isPortrait={isPortrait}
                disabled={!hasReport}
                disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
            >
                <AccordionSummary className="accordion-with-subtitle">
                    <Box>
                        <Typography>{t("events.report")}</Typography>
                        {selectedReport.lastModified && (
                            <Typography className="accordion-subtitle">
                                {`${t("events.creation_date")}: ${dayjs(selectedReport.lastModified).format(t("date.format.shortest"))}`}
                            </Typography>
                        )}
                    </Box>
                </AccordionSummary>

                <AccordionDetails>
                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                        {moreThanOneCam && (
                            <CameraSelection
                                value={report.selectedCameraId}
                                onChange={setReportCameraId}
                                availableCameras={availableCameras}
                            />
                        )}

                        {hasReport && !report.loading && availableCameras.length === 0 && (
                            <Alert
                                variant="filled"
                                severity="error"
                                sx={{ mb: 2, alignItems: "center", userSelect: "none" }}
                            >
                                {t("msg.no_file")}
                            </Alert>
                        )}

                        <Button
                            onClick={() => setOpenDialog(true)}
                            startIcon={<LibraryBooks />}
                            disabled={!canShowReport}
                            variant="contained"
                            size='large'
                            fullWidth
                        >
                            {t("actions.show")}
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <PdfDialog
                open={openDialog}
                setOpen={setOpenDialog}
                fileExists={fileExists}
                filePath={filePath}
                targetFilename={targetFilename}
            />
        </>
    )
}
